import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Radio, Server, Star } from "lucide-react";
import { ContactForm } from "../ContactForm";
import { whatsappLink, IMAGES } from "../../data/site";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { icon: Radio, label: "100% Live 1-to-1 Sessions" },
  { icon: Server, label: "24x7 Cloud Hardware Labs" },
  { icon: ShieldCheck, label: "450+ Placed in MNCs" },
  { icon: Star, label: "4.9/5 Student Rating" },
];

export const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <video
          ref={videoRef}
          data-testid="hero-bg-video"
          className="w-full h-full object-cover opacity-30 scale-110"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={IMAGES.hero}
        >
          <source src="/videos/hero-lab.webm" type="video/webm" />
          <source src={IMAGES.heroVideo} type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#060913]/70 via-[#060913]/85 to-[#060913]" />
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-blue-700/20 blur-[140px] animate-glow" />
      <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-sky-500/15 blur-[140px] animate-glow" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 mb-7"
            >
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-orange-400 font-semibold">
                India's First Truly 1-on-1 IT Security Academy
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>REAL ENGINEERS.</MaskedLine>
              <MaskedLine delay={0.3}>REAL LABS.</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="text-gradient-orange">REAL CAREERS.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            >
              Master <span className="text-sky-400 font-semibold">Network Security</span>,{" "}
              <span className="text-sky-400 font-semibold">Cloud Security</span> &{" "}
              <span className="text-sky-400 font-semibold">Cyber Security</span> in private live sessions with working
              engineers from Cisco, AWS, CrowdStrike & Microsoft — at the{" "}
              <span className="text-orange-400 font-semibold">most affordable pricing in the market</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                data-testid="hero-whatsapp-btn"
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 rounded-xl bg-[#25D366] hover:bg-[#1fb857] text-[#060913] font-display font-bold text-sm sm:text-base px-7 py-4 transition-all duration-300 shadow-[0_0_35px_rgba(37,211,102,0.3)] hover:shadow-[0_0_50px_rgba(37,211,102,0.45)] hover:-translate-y-0.5"
              >
                <svg viewBox="0 0 32 32" className="w-5 h-5 fill-current" aria-hidden="true">
                  <path d="M16 2.7C8.7 2.7 2.8 8.6 2.8 15.9c0 2.6.8 5 2.1 7L2.7 29.3l6.6-2.1c1.9 1.2 4.2 1.8 6.6 1.8 7.3 0 13.2-5.9 13.2-13.1S23.3 2.7 16 2.7zm6.6 15.8c-.4-.2-2.1-1.1-2.5-1.2-.3-.1-.6-.2-.8.2-.2.4-.9 1.2-1.1 1.4-.2.2-.4.3-.8.1-.4-.2-1.5-.6-2.9-1.9-1.1-1-1.8-2.2-2-2.6-.2-.4 0-.6.2-.8l.6-.7c.2-.2.3-.4.4-.6.1-.2 0-.5 0-.6-.1-.2-.8-2.1-1.1-2.9-.3-.8-.6-.7-.8-.7h-.7c-.2 0-.6.1-1 .5-.3.4-1.2 1.2-1.2 3s1.3 3.5 1.5 3.7c.2.2 2.5 3.9 6.1 5.4.9.4 1.5.6 2.1.8.9.3 1.7.2 2.3.1.7-.1 2.1-.9 2.4-1.7.3-.9.3-1.6.2-1.7-.1-.2-.4-.3-.8-.5z" />
                </svg>
                Chat on WhatsApp
              </a>
              <a
                data-testid="hero-explore-courses-btn"
                href="#courses"
                className="inline-flex items-center gap-2 rounded-xl border border-sky-500/40 text-sky-300 hover:text-white hover:border-sky-400 hover:bg-sky-500/10 font-display font-semibold text-sm sm:text-base px-7 py-4 transition-all duration-300 hover:-translate-y-0.5"
              >
                Explore Courses
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              {STATS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full glass-card border border-sky-500/20 px-4 py-2 text-xs font-medium text-slate-300"
                >
                  <Icon className="w-3.5 h-3.5 text-orange-500" />
                  {label}
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="glass-card rounded-2xl border border-sky-500/25 p-6 sm:p-8 shadow-[0_0_60px_rgba(29,78,216,0.15)]"
            data-testid="hero-contact-card"
          >
            <h3 className="font-display text-xl font-bold text-white">
              Book Your <span className="text-gradient-orange">Free Demo</span>
            </h3>
            <p className="mt-1.5 text-sm text-slate-400 mb-6">
              30 minutes. One trainer. Zero sales pitch — experience the 1-on-1 difference.
            </p>
            <ContactForm compact idPrefix="hero-contact" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
